import { useActiveCircleId } from '@/hooks/use-active-circle';
import { useWorkflowByDefnIdForRecipient } from '@/hooks/use-workflows';
import {
    getProgramPackClassFromMetadata,
    getProgramType,
    ProgramState,
    getProgramWorkflowIdFromMetadata,
    getProgramState,
} from '@rhg/utils/programs';
import {
    getWorkflowCurrentStep,
    getWorkflowPackClass,
} from '@rhg/utils/workflows';
import { useMemo } from 'react';
import { useProgramContext } from '../';
import { ProgramContextProps } from '../types';
import { ProgramContextWithWorkflowState } from './types';

export function useProgramContextWithWorkflow(
    props: ProgramContextProps
): ProgramContextWithWorkflowState {
    const {
        program: _program,
        programMetadata,
        packProgram,
        packClass: providedPackClass,
        _workflow,
        ...rest
    } = useProgramContext(props);
    const program = _program ?? programMetadata ?? packProgram;
    const workflowId = _workflow?.workflowDefn.defnId
        ? ''
        : getProgramWorkflowIdFromMetadata(program);
    const { activeCircleId } = useActiveCircleId();
    const workflowRequest = useWorkflowByDefnIdForRecipient({
        workflowDefnId: workflowId ?? '',
        circleId: activeCircleId,
    });
    const workflow = _workflow ?? workflowRequest?.workflow;
    // non-clinical programs will never fetch a workflow, so don't wait for it
    const canHaveWorkflow = program?.isClinical;
    const isFetched =
        Boolean(program) &&
        (!canHaveWorkflow ||
            Boolean(_workflow) ||
            Boolean(workflowId && workflowRequest?.isFetched));

    const programState = useMemo(() => {
        if (!program) {
            return ProgramState.Unspecified;
        }
        return getProgramState(program, workflow, _program?.guides);
    }, [workflow, program, _program?.guides]);
    const programType = useMemo(
        () => getProgramType(program, workflow),
        [program, workflow]
    );

    const currentWorkflowStep = useMemo(
        () => getWorkflowCurrentStep(workflow),
        [workflow]
    );

    const pollWorkflowSteps = useMemo(
        () =>
            workflow?.steps.filter(
                (step) =>
                    step.stepDefn.type === 'poll' && step.stepDefn.data.display
            ),
        [workflow?.steps]
    );
    const packClass = useMemo(() => {
        if (providedPackClass) {
            return providedPackClass;
        }
        const programPackClass = getProgramPackClassFromMetadata(program);
        if (programPackClass) {
            return programPackClass;
        }
        if (workflow) {
            return getWorkflowPackClass(workflow);
        }
        return undefined;
    }, [program, providedPackClass, workflow]);

    return useMemo(
        () => ({
            // pass-throughs
            program: _program,
            programMetadata,
            packProgram,
            ...rest,
            // overwrites
            packClass,
            programType,
            isFetched,
            // net-new properties
            workflow,
            programState,
            currentWorkflowStep,
            pollWorkflowSteps,
        }),
        [
            _program,
            currentWorkflowStep,
            isFetched,
            packClass,
            packProgram,
            pollWorkflowSteps,
            programMetadata,
            programState,
            programType,
            rest,
            workflow,
        ]
    );
}
